if (!window.__featuredProductSliderInit) {
window.__featuredProductSliderInit = true;

class FeaturedProductSlider {
  constructor(element) {
    this.wrapper  = element;
    this.section  = element.closest('[data-section-id]');
    this.track    = element.querySelector('[data-track]');
    this.prevBtn  = this.section?.querySelector('[data-prev]');
    this.nextBtn  = this.section?.querySelector('[data-next]');
    this.mobilePrevBtn = this.section?.querySelector('[data-prev-mobile]');
    this.mobileNextBtn = this.section?.querySelector('[data-next-mobile]');

    this.controller = new AbortController();
    this.originalTabindex = element.getAttribute('tabindex');
    this.idx    = 0;
    this.cols   = this.getCols();
    this.sw     = 0;
    this.gap    = 0;
    this.max    = 0;
    this._offset = 0;

    this._t = {
      active: false,
      x0: 0, y0: 0,
      x: 0,
      dir: null,
      base: 0,
      vx: 0,
      lx: 0, lt: 0
    };

    if (this.section && this.section.dataset.shuffle === 'true') {
      this.shuffleSlides();
    }

    this.slides = [...element.querySelectorAll('.featured-product-slide')];
    this.init();
  }

  listen(target, type, callback, options = {}) {
    target?.addEventListener(type, callback, {
      ...(typeof options === 'boolean' ? { capture: options } : options),
      signal: this.controller.signal
    });
  }

  destroy() {
    this.newestController?.abort();
    this.controller.abort();
    cancelAnimationFrame(this.frame);
    clearTimeout(this.resizeTimer);
    this._t.active = false;
    this.track.style.cursor = '';
    if (this.originalTabindex === null) this.wrapper.removeAttribute('tabindex');
    else this.wrapper.setAttribute('tabindex', this.originalTabindex);
    delete this.wrapper.dataset.sliderInit;
    delete this.wrapper.featuredProductSlider;
  }

  // Keep this server-rendered pool stable for the tab's visit. Never reorder
  // on interaction. If storage is blocked, use a deterministic pool order.
  shuffleSlides() {
    const track = this.track;
    if (!track) return;
    const slides = [...track.querySelectorAll('.featured-product-slide')];
    if (slides.length < 3) return;
    const entries = slides.map(slide => ({ slide, id: slide.dataset.productId }));
    if (entries.some(entry => !entry.id) || new Set(entries.map(entry => entry.id)).size !== entries.length) return;
    entries.sort((a, b) => a.id.localeCompare(b.id));
    const key = 'collino:featured-order:v1:' + JSON.stringify([
      window.location.hostname, window.Shopify?.country || '',
      window.Shopify?.currency?.active || '', this.section.dataset.sectionId,
      entries.map(entry => entry.id)
    ]);
    let seed = 2166136261;
    for (let i = 0; i < key.length; i += 1) seed = Math.imul(seed ^ key.charCodeAt(i), 16777619) >>> 0;
    try {
      const stored = window.sessionStorage.getItem(key);
      if (stored !== null && /^\d+$/.test(stored) && Number(stored) <= 4294967295) {
        seed = Number(stored);
      } else {
        const fresh = Math.floor(Math.random() * 4294967296);
        window.sessionStorage.setItem(key, String(fresh));
        seed = fresh;
      }
    } catch (_) { /* Deterministic fallback also survives editor reinsertion. */ }
    for (let i = entries.length - 1; i > 0; i -= 1) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      const j = Math.floor((seed / 4294967296) * (i + 1));
      [entries[i], entries[j]] = [entries[j], entries[i]];
    }
    const frag = document.createDocumentFragment();
    entries.forEach(({ slide }) => frag.appendChild(slide));
    track.appendChild(frag);
  }

  getCols() {
    const w = window.innerWidth;
    if (w <= 480)  return 2;
    if (w <= 720)  return 2;
    if (w <= 1024) return 3;
    if (w <= 1400) return 4;
    return 5;
  }

  get _gap() { return window.innerWidth <= 640 ? 10 : 14; }
  get _step() { return this.sw + this.gap; }
  get _maxOff() { return Math.max(0, this.max * this._step); }

  calc() {
    const vp  = this.wrapper.querySelector('.featured-products-slider__viewport');
    if (!vp) return;
    const vpW = vp.getBoundingClientRect().width;
    this.gap  = this._gap;
    this.sw   = (vpW - this.gap * (this.cols - 1)) / this.cols;
    this.max  = Math.max(0, this.slides.length - this.cols);
    this.slides.forEach((s, i) => {
      s.style.width       = `${this.sw}px`;
      s.style.minWidth    = `${this.sw}px`;
      s.style.marginRight = i < this.slides.length - 1 ? `${this.gap}px` : '0';
    });
  }

  updateUI() {
    if (this.prevBtn) this.prevBtn.disabled = this.idx <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.idx >= this.max;
    if (this.mobilePrevBtn) this.mobilePrevBtn.disabled = this.idx <= 0;
    if (this.mobileNextBtn) this.mobileNextBtn.disabled = this.idx >= this.max;
  }

  _setRaw(offset) {
    this._offset = offset;
    this.track.style.transition = 'none';
    this.track.style.transform  = `translateX(${-offset}px)`;
  }

  go(i, anim = true) {
    this.idx     = Math.max(0, Math.min(i, this.max));
    this._offset = this.idx * this._step;
    if (anim && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.track.style.transition = 'transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94)';
    } else {
      this.track.style.transition = 'none';
    }
    this.track.style.transform = `translateX(${-this._offset}px)`;
    this.updateUI();
  }

  next() { this.go(this.idx + 1); }
  prev() { this.go(this.idx - 1); }

  _touchStart(e) {
    const t0 = e.touches[0];
    this._t = {
      active: true,
      x0: t0.clientX, y0: t0.clientY,
      x:  t0.clientX,
      dir: null,
      base: this._offset,
      vx: 0,
      lx: t0.clientX, lt: Date.now()
    };
    this.track.style.transition = 'none';
  }

  _touchMove(e) {
    const t = this._t;
    if (!t.active) return;

    const touch = e.touches[0];
    const dx = touch.clientX - t.x0;
    const dy = touch.clientY - t.y0;

    if (t.dir === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      // 1.2x: un diagonal sucio de pulgar cuenta como scroll, no como swipe
      t.dir = Math.abs(dx) > 1.2 * Math.abs(dy) ? 'h' : 'v';
    }

    if (t.dir === 'v') {
      t.active = false;
      return;
    }

    e.preventDefault();

    const now = Date.now();
    const dt  = now - t.lt;
    if (dt > 0) t.vx = (touch.clientX - t.lx) / dt;
    t.lx = touch.clientX;
    t.lt = now;
    t.x  = touch.clientX;

    let raw = t.base - dx;
    if (raw < 0) {
      raw = raw * 0.25;
    } else if (raw > this._maxOff) {
      raw = this._maxOff + (raw - this._maxOff) * 0.25;
    }

    this._setRaw(raw);
  }

  _touchEnd(e) {
    const t = this._t;
    if (!t.active) return;
    t.active = false;

    if (t.dir !== 'h') return;

    const dx = t.x - t.x0;

    const projected = this._offset - (t.vx * 150);
    let   target    = Math.round(projected / this._step);

    if (dx < -35 || t.vx < -0.25) target = Math.max(target, this.idx + 1);
    if (dx >  35 || t.vx >  0.25) target = Math.min(target, this.idx - 1);

    this.go(Math.max(0, Math.min(target, this.max)));
  }

  _touchCancel() {
    if (!this._t.active) return;
    this._t.active = false;
    this.go(this.idx);
  }

  resize() {
    const next = this.getCols();
    if (next !== this.cols) { this.cols = next; this.idx = 0; }
    this.calc();
    this.go(this.idx, false);
  }

  init() {
    this.frame = requestAnimationFrame(() => {
      this.calc();
      this.updateUI();
    });

    this.listen(this.prevBtn, 'click', () => this.prev());
    this.listen(this.nextBtn, 'click', () => this.next());
    this.listen(this.mobilePrevBtn, 'click', () => this.prev());
    this.listen(this.mobileNextBtn, 'click', () => this.next());

    this.listen(this.track, 'touchstart',  e => this._touchStart(e),  { passive: true });
    this.listen(this.track, 'touchmove',   e => this._touchMove(e),   { passive: false });
    this.listen(this.track, 'touchend',    e => this._touchEnd(e),    { passive: true });
    this.listen(this.track, 'touchcancel', ()  => this._touchCancel(), { passive: true });


    this.listen(this.track, 'click', e => {
      if (e.detail !== 0 && (this._t.dir === 'h' || Math.abs(this._t.x - this._t.x0) > 10)) {
        e.preventDefault();
        e.stopPropagation();
        this._t.dir = null;
        this._t.x = this._t.x0;
      }
    }, true);

    let mx0 = 0, mbase = 0, mdown = false, mmoved = false;
    this.listen(this.track, 'mousedown', e => {
      if (e.button !== 0) return;
      mdown = true; mmoved = false;
      mx0   = e.clientX;
      mbase = this._offset;
      this.track.style.transition = 'none';
      this.track.style.cursor = 'grabbing';
    });
    this.listen(window, 'mousemove', e => {
      if (!mdown) return;
      const dx = e.clientX - mx0;
      if (Math.abs(dx) > 5) mmoved = true;
      if (!mmoved) return;
      let raw = mbase - dx;
      if (raw < 0) raw = raw * 0.25;
      else if (raw > this._maxOff) raw = this._maxOff + (raw - this._maxOff) * 0.25;
      this._setRaw(raw);
    });
    this.listen(window, 'mouseup', e => {
      if (!mdown) return;
      mdown = false;
      this.track.style.cursor = '';
      if (!mmoved) return;
      const dx = e.clientX - mx0;
      let target = Math.round(this._offset / this._step);
      if (dx < -40) target = Math.max(target, this.idx + 1);
      if (dx >  40) target = Math.min(target, this.idx - 1);
      this.go(Math.max(0, Math.min(target, this.max)));
    });
    this.listen(this.track, 'click', e => {
      if (mmoved && e.detail !== 0) { e.preventDefault(); e.stopPropagation(); }
      mmoved = false;
    }, true);

    this.listen(this.track, 'focusin', e => {
      const slide = e.target.closest('.featured-product-slide');
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      if (index < this.idx) this.go(index, false);
      else if (index >= this.idx + this.cols) this.go(index - this.cols + 1, false);
      const viewport = this.wrapper.querySelector('.featured-products-slider__viewport');
      if (viewport) viewport.scrollLeft = 0;
    });
    this.wrapper.setAttribute('tabindex', '0');
    this.listen(this.wrapper, 'keydown', e => {
      if (e.target !== this.wrapper) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        if (e.key === 'ArrowRight') this.next();
        else this.prev();
      }
    });

    this.listen(window, 'resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.resize(), 150);
    });
  }
}

window.FeaturedProductSlider = FeaturedProductSlider;

window.__initFeaturedSliders = (root = document) => {
  root.querySelectorAll('[data-slider]').forEach(el => {
    if (!el.closest('.featured-products-slider') || el.dataset.sliderInit || !el.querySelector('[data-track]')) return;
    el.dataset.sliderInit = 'true';
    el.featuredProductSlider = new window.FeaturedProductSlider(el);
    if (el.featuredProductSlider.section?.hasAttribute('data-newest-home')) {
      window.CollinoNewestProducts?.attach(el.featuredProductSlider);
    }
  });
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => window.__initFeaturedSliders(), { once: true });
else window.__initFeaturedSliders();
document.addEventListener('shopify:section:load', e => window.__initFeaturedSliders(e.target));
document.addEventListener('shopify:section:unload', e => {
  e.target.querySelectorAll('[data-slider]').forEach(el => el.featuredProductSlider?.destroy());
});

}
